import { useEffect, useState } from 'react'
import { useActivity } from '../api/queries'
import type { ActivityEntry } from '../api/types'
import { EmptyState, ErrorBox, Loading } from './ui'
import { formatDate, formatTime } from '../lib/format'

type Filter = 'all' | 'tasks' | 'status'

/**
 * "Statusskift" er både sagens status og opgavernes - det er dér, man ser, hvem
 * der lukkede hvad. Serveren filtrerer, så sidetallet passer til det viste.
 */
const FILTERS: { key: Filter; label: string; query: Record<string, string> }[] = [
  { key: 'all', label: 'Alt', query: {} },
  { key: 'status', label: 'Statusskift', query: { field: 'status' } },
  { key: 'tasks', label: 'Opgaver', query: { entityType: 'Task' } }
]

const PAGE_SIZE = 50

/**
 * Knap og vindue med sagens aktivitetslog (beslutning 086). Vises kun for
 * Superadmin, Administrator og Projektleder - serveren afviser alle andre.
 */
export function ProjectActivityLogButton({ projectId, projectName }: { projectId: number; projectName: string }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button className="btn btn--sm" onClick={() => setOpen(true)}>
        📝 Aktivitetslog
      </button>
      {open && <ProjectActivityLogDialog projectId={projectId} projectName={projectName} onClose={() => setOpen(false)} />}
    </>
  )
}

function ProjectActivityLogDialog({
  projectId,
  projectName,
  onClose
}: {
  projectId: number
  projectName: string
  onClose: () => void
}) {
  const [filter, setFilter] = useState<Filter>('all')
  const [page, setPage] = useState(1)
  const active = FILTERS.find((f) => f.key === filter)!
  const { data, isLoading, error } = useActivity({ projectId, page, pageSize: PAGE_SIZE, ...active.query })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-label={`Aktivitetslog for ${projectName}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal__card modal__card--wide">
        <div className="row row--between" style={{ alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <h2 style={{ fontSize: 18 }}>Aktivitetslog</h2>
            <div className="small muted">{projectName}</div>
          </div>
          <button className="btn btn--sm" onClick={onClose}>
            Luk
          </button>
        </div>

        <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`btn btn--sm${f.key === filter ? ' btn--primary' : ''}`}
              aria-pressed={f.key === filter}
              onClick={() => {
                setFilter(f.key)
                setPage(1)
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {isLoading && <Loading />}
        {error && <ErrorBox error={error} />}
        {data && data.items.length === 0 && (
          <EmptyState>{filter === 'all' ? 'Der er ikke sket noget på sagen endnu.' : 'Ingen poster med dette filter.'}</EmptyState>
        )}

        {data && data.items.length > 0 && (
          <>
            {groupByDay(data.items).map(([day, entries]) => (
              <div key={day} style={{ marginBottom: 14 }}>
                <div className="tiny muted" style={{ fontWeight: 700, marginBottom: 4 }}>
                  {day}
                </div>
                <div className="stack" style={{ gap: 0 }}>
                  {entries.map((entry) => (
                    <div
                      key={entry.id}
                      className="row"
                      style={{ alignItems: 'baseline', gap: 10, padding: '6px 0', borderTop: '1px solid var(--border)' }}
                    >
                      <span className="tiny muted" style={{ minWidth: 40 }}>
                        {formatTime(entry.createdAt)}
                      </span>
                      <span className="small">{describe(entry)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {data.total > PAGE_SIZE && (
              <div className="row" style={{ marginTop: 8 }}>
                <button className="btn btn--sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                  Nyere
                </button>
                <span className="small muted">
                  Side {data.page} af {Math.ceil(data.total / data.pageSize)} · {data.total} poster
                </span>
                <button
                  className="btn btn--sm"
                  disabled={data.page * data.pageSize >= data.total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Ældre
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/** Teksten fra serveren siger allerede hvem og hvad ("Jens ændrede ... til Færdig"). */
function describe(entry: ActivityEntry) {
  return entry.summary ?? `${entry.actorName}: ${entry.action} ${entry.entityName ?? ''}`.trim()
}

/** Posterne kommer nyeste først; dagene beholder den rækkefølge. */
function groupByDay(entries: ActivityEntry[]): [string, ActivityEntry[]][] {
  const groups = new Map<string, ActivityEntry[]>()
  for (const entry of entries) {
    const day = formatDate(entry.createdAt)
    const list = groups.get(day)
    if (list) list.push(entry)
    else groups.set(day, [entry])
  }
  return [...groups.entries()]
}
